import request from '@/api/request'

// 出库明细的空间分摊（打印按空间分组用，FIFO 口径）
export interface OutboundSpaceAllocation {
  spaceId: number | null
  quantity: number
}

// 来源销售订单的安装空间
export interface OutboundSpace {
  id: number
  orderId?: number
  floorName: string
  spaceName: string
  sortOrder?: number
}

export interface OutboundItem {
  id?: number
  itemId?: number          // 待出库单明细ID（确认出库模式行）
  outboundId?: number      // 归属待出库单ID（确认出库模式行）
  productId: number
  productName?: string
  productCode?: string
  spec?: string
  unit?: string
  quantity: number
  unitPrice?: number
  discount?: number
  discountedPrice?: number  // 折后单价（从销售订单传递）
  installFeePerUnit?: number
  warehouseId?: number
  locationId?: number
  sourceType?: string
  sourceId?: number
  orderId?: number          // 归属销售订单ID（合并出库时标识明细来源）
  orderNo?: string          // 归属销售订单号（展示用）
  itemOrderNo?: string      // 后端详情返回的归属订单号
  spaceAllocations?: OutboundSpaceAllocation[]  // 后端详情返回的空间分摊
  actualStock?: number | null    // 后端详情返回的实际库存（现货）
  availableStock?: number | null // 后端详情返回的可用库存（现货+采购在途-销售待出）
  remark?: string
  product?: any
}

// 出库单关联的销售订单（合并出库）
export interface OutboundSourceOrder {
  orderId: number
  orderNo?: string
  amount?: number
}

export interface OutboundMember {
  employeeId: number | undefined
  employeeName?: string
  ratio?: number | null
  commissionRate?: number | null
}

export interface Outbound {
  id?: number
  outboundNo?: string
  type: 'sales' | 'purchase_return' | 'transfer_out' | 'stocktake_out' | 'adjust' | 'repair'
  warehouseId: number
  customerId?: number
  sourceOrderId?: number
  sourceOrderNo?: string
  sourceOrderIds?: number[]        // 合并出库：多张销售订单ID
  sourceOrders?: OutboundSourceOrder[]  // 后端返回的关联订单集合
  totalAmount?: number
  status?: string
  remark?: string
  items: OutboundItem[]
  spaces?: OutboundSpace[]       // 后端详情返回的来源订单安装空间
  salesMembers?: OutboundMember[]
  installMembers?: OutboundMember[]
  warehouse?: any
  customer?: any
  approver?: any
  creator?: any
}

export function getOutboundList(params: any) {
  return request({
    url: '/outbounds',
    method: 'get',
    params
  })
}

export function getOutboundById(id: number) {
  return request({
    url: `/outbounds/${id}`,
    method: 'get'
  })
}

// 出库单由系统自动生成，不支持手工创建（POST /outbounds 已封堵）

// 确认出库运费（2026-10-13）：>0 生效；公司承担/向客户另收均生成运费报销单（现付=付款员工报销，挂账=物流公司应付）
export interface ConfirmOutboundFreight {
  amount: number
  payerType: 'company' | 'customer'   // company公司承担 / customer向客户另收（计入该单客户应收）
  paymentType: 'cash' | 'credit'      // cash现付 / credit挂账物流公司
  logisticsCompanyId?: number         // 物流公司档案ID（优先）
  logisticsCompany?: string           // 或直接填名称（后端自动建档）
  trackingNo?: string
  images?: string[]                   // 运单图片（非必填，最多3张）
  accountId?: number                  // 挂账应付账户（credit 建议必填）
  employeeId?: number                 // 现付=付款员工即报销人（缺省回退操作人绑定员工）
}

// 确认出库：从待出库单扣减数量，生成已完成出库单直接生效（需 outbound:approve 权限）
export interface ConfirmOutboundData {
  receipts: Array<{
    outboundId: number
    items: Array<{ itemId: number; quantity: number; locationId?: number }>
  }>
  salesMembers?: OutboundMember[]
  installMembers?: OutboundMember[]
  remark?: string
  freight?: ConfirmOutboundFreight
}

export function confirmOutbound(data: ConfirmOutboundData) {
  return request({
    url: '/outbounds/confirm',
    method: 'post',
    data
  })
}

// 试算出库金额（与后端创建/编辑同口径：末批清算按订单应收，含整单优惠）
export function calcOutboundAmount(data: { sourceOrderIds: number[]; items: any[]; excludeOutboundId?: number }) {
  return request({
    url: '/outbounds/calc-amount',
    method: 'post',
    data
  })
}

export function updateOutbound(id: number, data: Partial<Outbound>) {
  return request({
    url: `/outbounds/${id}`,
    method: 'put',
    data
  })
}

export function deleteOutbound(id: number) {
  return request({
    url: `/outbounds/${id}`,
    method: 'delete'
  })
}

// 出库单无需审核（确认出库即生效），原 approve/complete 接口已移除

export function cancelOutbound(id: number) {
  return request({
    url: `/outbounds/${id}/cancel`,
    method: 'post'
  })
}

// 撤销出库审核（completed → draft，回滚库存，需 outbound:revoke 权限）
export function revokeOutbound(id: number) {
  return request({
    url: `/outbounds/${id}/revoke`,
    method: 'post'
  })
}

export function getOutboundStats() {
  return request({
    url: '/outbounds/stats',
    method: 'get'
  })
}

export function getDeliveredQuantityBySourceOrder(sourceOrderId: number) {
  return request({
    url: `/outbounds/source-order/${sourceOrderId}/delivered`,
    method: 'get'
  })
}

// 待出库单明细行（粒度 = 待出库单明细，数量为剩余待出量，按销售单行粒度带空间）
export interface PendingOutboundItem {
  itemId: number
  orderId: number
  orderNo: string
  productId: number
  productName: string
  productCode: string
  unit: string
  pendingQuantity: number
  unitPrice: number
  locationId?: number | null
  spaceId?: number | null
  remark?: string | null
  warehouseStock?: number // 本仓现货（出库组仓库）
  totalStock?: number     // 全仓现货
}

// 待出库单（销售审批自动生成，draft）
export interface PendingOutboundDoc {
  outboundId: number
  outboundNo: string
  items: PendingOutboundItem[]
}

// 待出库分组（客户 + 仓库，同组可合并确认出库）
export interface PendingOutboundGroup {
  customerId: number
  customerName: string
  warehouseId: number
  warehouseName: string
  docs: PendingOutboundDoc[]
}

export function getPendingOutboundItems(params?: any) {
  return request({
    url: '/outbounds/pending-items',
    method: 'get',
    params
  })
}
